// How the sidebar is assembled, with no idea what is installed.
//
// Split out from navigation.js so it imports nothing but the permission rule
// beside it, which is pure too: the registry is built by Vite from whatever
// modules are present, which makes it unloadable outside a Vite build and
// makes any test that touches it a test about today's repository rather than
// about the rule.

import { allows } from './access.js'

/**
 * Where an entry sits inside its group when it does not say.
 *
 * Order used to be an accident of import order, which was survivable while the
 * platform's screens were spread over two short sections. Now that they share
 * one block of eight, `Operación` landing second because of where its package
 * is imported is a visible bug rather than a detail.
 *
 * Entries that do not care keep registration order among themselves — the sort
 * is stable, and a module's screens stay in the order the module declared them.
 */
const DEFAULT_ORDER = 50

/**
 * Cuántos colores reparte el menú entre los módulos.
 *
 * Ocho, y no uno por módulo: con más de ocho tonos distinguibles a la vez el
 * lector deja de leer un color y empieza a leer un arcoíris. El color NO dice
 * qué módulo es —eso lo dice el nombre, que va escrito encima de la banda—
 * sino que estas dos entradas son del mismo. Para eso ocho sobran, porque lo
 * que se compara es una banda con la de al lado.
 *
 * Se reparten por ORDEN DE APARICIÓN y no por el nombre del módulo. Repartir
 * por nombre —un hash— parecía mejor porque daba el mismo color al mismo
 * módulo en todas las instancias, y era peor: con los nombres que hay,
 * `attendance` y `work-orders` caían en el mismo tono, que son justo las dos
 * bandas que se ven a la vez en el menú. Por orden es imposible que dos vecinas
 * coincidan mientras haya ocho o menos, que es el caso real, y a partir de ahí
 * lo peor que pasa es que se repita una lejana.
 */
export const BAND_COLOURS = 8

/**
 * The sections of the sidebar, in the order they appear.
 *
 * Three sections, and the split is by **who owns the screen**, not by how often
 * it is used — mixing the two axes is what put a module's own screen (`Mi
 * jornada`) next to Home and Notifications while the platform's screens sat
 * spread across three separate sections.
 *
 *   main      where you land. No heading — the top of a sidebar does not need
 *             a label saying it is the top.
 *   business  everything the installed modules contribute. This is what the
 *             client paid for, so it stays open and sits above our own.
 *   platform  everything the platform owns. One place, folded away.
 *
 * `collapsible: false` is for the section you land in — a single entry and a
 * toggle would be more chrome than navigation.
 *
 * `startCollapsed` is what keeps the platform out of the way: it is visited
 * occasionally, while the modules a client actually bought are the work. With
 * ten modules installed, a sidebar that opens with every platform entry
 * expanded is a sidebar that makes the client scroll past our furniture to
 * reach their own. A folded section opens by itself while you are inside one of
 * its screens — and closes again if you say so, because a fold the reader cannot
 * close is not a fold (see `isGroupOpen` in navState.js).
 *
 * Notifications and Support sit in `platform` with the rest of what the
 * platform owns, and they lead the block. Both are reachable without opening
 * it: the bell in the top bar carries the unread count, and the assistant
 * answers a question before anyone goes looking for a menu entry.
 */
export const GROUPS = [
  // No labelKey: this section renders without a heading.
  { id: 'main', collapsible: false },
  { id: 'business', labelKey: 'nav.business' },
  // Same key as the version in the footer, and deliberately so: both name the
  // same thing to the reader.
  { id: 'platform', labelKey: 'nav.platform', startCollapsed: true },
]

/**
 * The entries a given user may actually see, grouped and sorted.
 *
 * An entry with no permission is visible to anyone signed in.
 *
 * ── Entries with children ────────────────────────────────────────────────────
 * An entry may be a folder rather than a link:
 *
 *     { to: '/x', labelKey: 'x.title', icon: 'file', group: 'business',
 *       children: [{ to: '/x/settings', labelKey: 'x.settings.title',
 *                    permission: 'x:admin' }] }
 *
 * It exists because a module is not always one screen. A module with six pages
 * either takes six of the few slots in the sidebar — pushing whatever is
 * installed next below the fold — or hides five of them behind a page the user
 * has to reach first and then look around in. Neither is navigation.
 *
 * The rule is per entry and not per module: this file has no idea which module
 * anything came from, and an entry with no children renders exactly as it
 * always did.
 *
 * ── Entries another module takes over ────────────────────────────────────────
 * An entry may name the module that makes it redundant:
 *
 *     { to: '/quotes/catalog', labelKey: '…', supersededBy: 'inventory' }
 *
 * Two modules can own halves of the same idea, and which half is on screen
 * depends on what the client bought. Quotes keeps a rate catalogue so a company
 * that bought it alone can price a bolt; the day they install the warehouse,
 * that catalogue is a mirror of somebody else's screen, and two menu entries for
 * one list is how people end up editing the copy that is not the original.
 *
 * The entry is dropped, not disabled, and its route stays registered: a link
 * that already exists keeps working, and the module that took over is where the
 * screen is looked for from then on.
 *
 * Supersession is checked before permissions, and a superseded parent takes its
 * children with it — what is gone is the whole branch, not a row of it.
 *
 * ── Entradas que necesitan OTRO módulo (`requires`) ──────────────────────────
 * La otra cara de `supersededBy`. Una entrada puede nombrar los módulos sin los
 * que no tiene sentido:
 *
 *     { to: '/purchases/tenders', labelKey: '…', requires: ['construction'] }
 *
 * Sale solo si TODOS los de la lista están montados en esta instancia —el Y,
 * no el O—. Es la pieza que hace falta para que una función viva en su módulo
 * dueño y se encienda solo cuando está el otro: el comparativo de ofertas es de
 * Compras, pero sin Obras no hay paquetes que licitar, y una entrada que lleva
 * a una pantalla vacía es peor que no tenerla.
 *
 * Tres diferencias con `supersededBy`, y las tres son a propósito:
 *
 *   1. Mira lo MONTADO (`/health`, igual que `module`), no lo que trae el
 *      paquete. Con la misma imagen corriendo instancias distintas, «viene en
 *      la carpeta» no dice si el otro módulo contesta.
 *   2. Mientras `/health` no ha contestado responde como `isMounted`: sí para
 *      todo lo que viene en el paquete. Esconder y enseñar trescientos
 *      milisegundos después es peor que lo contrario.
 *   3. La RUTA también obedece. Una entrada sustituida deja la ruta viva para
 *      que un enlace viejo siga funcionando; esta no, porque la pantalla no
 *      funcionaría: el router lee `meta.requires` (la misma forma) y manda a
 *      «no encontrado» (ver router.js).
 *
 * Vale en una entrada padre —se lleva a sus hijas— y en una hija sola. Una
 * lista vacía no exige nada: el Y de ninguna condición es cierto.
 *
 * A child the user cannot open is dropped here, before it is drawn. A parent
 * left with no children still renders as an ordinary link — a folder with
 * nothing in it is a dead end, and its own page is usually the one screen the
 * user did have the right to see.
 *
 * Nothing is mutated. The navigation arrays a module exports are module-level
 * state in the bundle, so filtering them in place would let the first user's
 * permissions decide what everybody who signs in afterwards on that tab sees.
 *
 * ── De qué módulo es cada entrada ────────────────────────────────────────────
 * Una entrada puede venir marcada con el módulo que la aporta (`module`), y el
 * registro las marca todas. Cuando un módulo aporta MÁS DE UNA a la misma
 * sección, sus entradas salen juntas y con una banda: el nombre del módulo
 * encima y un filete de color a la izquierda.
 *
 * Es la respuesta a una pregunta que el menú no contestaba. «Mi jornada» y
 * «Fichajes» son la misma cosa comprada —la de la persona y la del despacho— y
 * en la lista aparecían como dos productos; con once módulos delante, un
 * visitante no tiene forma de saber cuántas cosas está mirando.
 *
 * Solo cuando son varias, y esto es la mitad del diseño: una banda sobre una
 * sola entrada no agrupa nada, repite el nombre de la entrada en pequeño encima
 * de sí misma y convierte un menú de once líneas en uno de veintidós. Un módulo
 * de una pantalla se dibuja exactamente como siempre.
 */
/**
 * Junta las entradas de un mismo módulo y les pone su banda.
 *
 * Dos cosas a la vez, y las dos hacen falta:
 *
 *   1. **Las agrupa.** El orden lo decide `order`, así que dos entradas de un
 *      módulo pueden acabar separadas por la de otro. Se reordena poniendo cada
 *      módulo entero en el sitio donde caía su PRIMERA entrada, con lo que el
 *      orden que el módulo pidió se respeta y lo único que cambia es que sus
 *      entradas quedan seguidas. Sin esto la banda tendría agujeros.
 *   2. **Las marca.** `band` lleva el módulo, si es la primera y si es la
 *      última, que es lo que la pantalla necesita para dibujar el filete de
 *      arriba abajo y escribir el nombre una sola vez.
 *
 * Las entradas sin módulo —las del propio armazón: Inicio, Notificaciones,
 * Soporte— no se tocan y salen con `band: null`.
 */
function inBands(items) {
  const counts = new Map()
  for (const item of items) {
    if (!item.module) continue
    counts.set(item.module, (counts.get(item.module) ?? 0) + 1)
  }

  // Reagrupado estable: cada módulo con más de una entrada viaja entero al
  // sitio de la primera. Los demás se quedan donde estaban.
  const ordered = []
  const placed = new Set()
  for (const item of items) {
    if (!item.module || counts.get(item.module) < 2) { ordered.push(item); continue }
    if (placed.has(item.module)) continue
    placed.add(item.module)
    ordered.push(...items.filter(other => other.module === item.module))
  }

  // El color se reparte aquí, que es el único sitio donde se sabe en qué orden
  // salen las bandas.
  const colours = new Map()
  for (const item of ordered) {
    if (!item.module || counts.get(item.module) < 2 || colours.has(item.module)) continue
    colours.set(item.module, (colours.size % BAND_COLOURS) + 1)
  }

  return ordered.map((item, index) => {
    if (!item.module || counts.get(item.module) < 2) return { ...item, band: null }
    return {
      ...item,
      band: {
        module: item.module,
        colour: colours.get(item.module),
        first: ordered[index - 1]?.module !== item.module,
        last: ordered[index + 1]?.module !== item.module,
      },
    }
  })
}

/**
 * @param {Function} [isInstalled]  ¿ESE OTRO módulo está? Contesta a la
 *   sustitución: con almacén instalado, el catálogo de materiales es suyo. Sin
 *   pasarla, nada sustituye a nada, que es lo que hacía antes de existir.
 * @param {Function} [isMounted]  ¿el módulo de ESTA entrada lo ha arrancado la
 *   instancia? Sin pasarla no se esconde nada.
 *
 * Son dos preguntas y no una, aunque se parezcan: la primera mira un módulo
 * distinto del de la entrada, y sus respuestas por defecto son opuestas —«no
 * hay nadie que sustituya» y «no escondas nada»—. Juntarlas en una sola función
 * hacía que el valor por defecto significara «esconde todas las entradas de
 * todos los módulos», que es una mina para quien la llame mañana.
 */
export function buildGroups(entries, can, isInstalled = () => false, isMounted = () => true) {
  const allowed = (item) => allows(item.permission, can)

  /**
   * Está en esta instancia: ni la sustituye otro módulo, ni es de un módulo que
   * esta instancia no ha arrancado.
   *
   * Lo segundo hacía falta y no estaba. El razonamiento escrito era que un
   * módulo que no arranca no publica sus permisos, así que sus entradas no le
   * salen a nadie — y es cierto para todo el mundo MENOS para quien lleva el
   * comodín. Un superadmin tiene `['*']`, que satisface cualquier permiso, así
   * que veía las veinticinco entradas en una instancia que monta dos: cada una
   * llevaba a una pantalla que pide sus datos y recibe «No hemos encontrado lo
   * que buscabas».
   *
   * Y el superadmin no es un caso raro: es quien entra el primer día en la
   * instancia de un cliente, y quien entra en una demo montada para una reunión.
   */
  const present = (item) => (!item.supersededBy || !isInstalled(item.supersededBy))
    && (!item.module || isMounted(item.module))
    && requirementsMet(item.requires, isMounted)
  const visible = (item) => present(item) && allowed(item)

  return GROUPS
    .map(group => ({
      ...group,
      items: entries
        .filter(item => item.group === group.id && present(item))
        .map(item => ({ ...item, children: (item.children ?? []).filter(visible) }))
        // A parent whose own permission the user lacks still appears when they
        // may open something inside it: the folder is a route to its children,
        // not a screen of its own.
        .filter(item => allowed(item) || item.children.length > 0)
        .sort((a, b) => (a.order ?? DEFAULT_ORDER) - (b.order ?? DEFAULT_ORDER)),
    }))
    .map(group => ({ ...group, items: inBands(group.items) }))
    .filter(group => group.items.length > 0)
}

/**
 * ¿Están montados todos los módulos que pide una entrada o una ruta?
 *
 * La misma pregunta para el menú (`requires` en la entrada) y para el router
 * (`meta.requires`), y por eso una sola función: una pantalla que se ve en el
 * menú y no se abre, o que se abre por URL y no está en el menú, es el mismo
 * defecto visto desde los dos lados (igual que `allows` en access.js).
 *
 * @param {string|string[]|null|undefined} requires  uno o varios módulos
 * @param {(name: string) => boolean} isMounted      ¿lo monta esta instancia?
 * @returns {boolean}  sin nada declarado, o con una lista vacía, cierto
 */
export function requirementsMet(requires, isMounted) {
  if (!requires) return true
  const names = Array.isArray(requires) ? requires : [requires]
  // TODOS. Una función que necesita Obras y Almacén no sirve con uno solo.
  return names.every(name => isMounted(name))
}

/**
 * La regla de «¿lo monta esta instancia?», sin saber de dónde salen los datos.
 *
 * Vive aquí, y no en navigation.js donde se usa, para poder probarla: aquel
 * fichero lee el registro de Vite y no se carga fuera de un build.
 *
 * @param {(name: string) => boolean} isInstalled  ¿viene en el paquete?
 * @param {Set<string>|null|undefined} mounted     lo que dijo `/health`;
 *   `null`/`undefined` mientras no ha contestado o si no contestó
 * @returns {(name: string) => boolean}
 */
export function mountedRule(isInstalled, mounted) {
  return (name) => {
    // Lo que no viene en el paquete no está, conteste lo que conteste el
    // servidor: no hay pantalla que enseñar.
    if (!isInstalled(name)) return false
    // Sin respuesta, sí. Ver la nota de `isMounted` en navigation.js.
    if (!mounted) return true
    return mounted.has(name)
  }
}
